import { useSearchParams } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import { logError } from 'utils/datadog';
import { CV_BUILDER_STEPS } from '../constants';
import { useCvBuilderData } from '../data/CvBuilderDataContext';
import { useStepNavigate } from './useStepNavigate';

export const useEditModeNavigation = () => {
  const [searchParams] = useSearchParams();
  const stepNavigate = useStepNavigate();
  const { saveDraft, loadDraft } = useCvBuilderData();
  const formikContext = useFormikContext<CVBuilderFormValues>();

  // Defensive programming to handle undefined context
  if (!formikContext) {
    throw new Error(
      'useEditModeNavigation must be used within a Formik context'
    );
  }

  const { values, resetForm } = formikContext;

  const isEditMode = searchParams.get('edit') === 'true';

  const handleUpdate = () => {
    // Fire-and-forget the save (the port handles persistence + errors and never
    // rejects), then return to the summary regardless of sync status.
    void saveDraft(values);
    stepNavigate(CV_BUILDER_STEPS.SUMMARY);
  };

  const handleDiscard = async () => {
    try {
      // Reload the last-persisted values (provider decides the source).
      const savedValues = await loadDraft();
      if (savedValues) {
        resetForm({ values: savedValues });
      }
      stepNavigate(CV_BUILDER_STEPS.SUMMARY);
    } catch (error) {
      logError('Failed to reload saved data', error);
      // Still navigate to summary even if reload fails
      stepNavigate(CV_BUILDER_STEPS.SUMMARY);
    }
  };

  return {
    isEditMode,
    handleUpdate,
    handleDiscard
  };
};
